import React from "react";
import { Link } from "react-router-dom";
import "../gi-registry/gi-registry.scss";
import PageHeader from "./ui/PageHeader";
import "./ui/karkhana-guide.scss";
import { kp } from "./karkhanaPaths";

type Step = { n: string; title: string; body: React.ReactNode };

const FLOW: Step[] = [
  {
    n: "1",
    title: "Set up once",
    body: (
      <>
        In <Link to={kp.settings()}>Settings</Link>: worker categories (K_, J_, D_…), then{" "}
        <Link to={kp.workers()}>workers</Link>, <Link to={kp.designs()}>designs</Link>,{" "}
        <Link to={kp.rateCards()}>rate cards</Link> (per-inch rates per design, approved by you) and{" "}
        <Link to={kp.timeRates()}>time rates</Link> for helpers and hourly work. Add supervisors last.
      </>
    ),
  },
  {
    n: "2",
    title: "Put looms on the list",
    body: (
      <>
        In <Link to={kp.rounds()}>Rounds</Link> → “+ Add looms”, type your loom numbers (e.g. N01–N44). Each loom also gets a
        fixed Loom ID (LM-…) in the loom register, used for geotag, photo and QR.
      </>
    ),
  },
  {
    n: "3",
    title: "Start a saree on each loom",
    body: (
      <>
        Open a loom in Rounds → “New saree”: pick the design, weaver and helper. HKP gives the next saree number (write it on the
        tag). One saree = one HKP product later.
      </>
    ),
  },
  {
    n: "4",
    title: "Enter work every day",
    body: (
      <>
        Office: <Link to={kp.dailyWork()}>Daily sheet</Link> — all looms for a date, like the DAILY WORK tab. Shop floor:{" "}
        <Link to={kp.rounds()}>Rounds</Link> on a phone, one loom at a time. Both start from the weaver and helper who worked
        last; change them for the day if someone else worked. Hours (helpers, warping, dyeing) go in{" "}
        <Link to={kp.attendance()}>Attendance</Link>; dyeing paid per job in <Link to={kp.jobWork()}>Job work</Link>.
      </>
    ),
  },
  {
    n: "5",
    title: "Pay weekly",
    body: (
      <>
        <Link to={kp.paySheets()}>Pay sheets</Link>: pick the dates, print the bilingual sheet, people sign, mark paid. Each
        person's running account is in <Link to={kp.pay()}>Pay</Link>; cash given to supervisors in{" "}
        <Link to={kp.cashBook()}>Cash book</Link>.
      </>
    ),
  },
];

const FAQ: { q: string; a: React.ReactNode }[] = [
  {
    q: "How does a supervisor log in?",
    a: (
      <>
        You add them in <Link to={kp.supervisors()}>Settings → Supervisors</Link> with their mobile number and a password (HKP
        suggests one). They sign in on the normal HKP login page with that mobile number and password, on their phone. They see
        only Rounds, Attendance, Pay and Waiting — nothing else in HKP — and can switch the screens to Hindi.
      </>
    ),
  },
  {
    q: "Where do loom numbers come from?",
    a: (
      <>
        You type them, the way the shop already names looms (N01, M04…), in Rounds → “+ Add looms” (a range like N01-N44 works).
        HKP also makes a fixed Loom ID (LM-XXXXXXXX) for each loom in the loom register; the Daily sheet shows both.
      </>
    ),
  },
  {
    q: "Where do saree numbers come from?",
    a: (
      <>
        A studio counter: you set the first number once (e.g. 14500), then each new saree gets the next one. Numbers already used in
        the old sheet are skipped after you import it.
      </>
    ),
  },
  {
    q: "Do workers become HKP users?",
    a: (
      <>
        No. Workers are karkhana records (code, name, category, phone) used for work and pay; they don't log in and don't appear in
        the HKP user list. Only supervisors you add get a login.
      </>
    ),
  },
  {
    q: "When is money calculated?",
    a: (
      <>
        On the server, from the approved rate card for the saree's design and the date: inches × rate, plus the alone allowance and
        the incentive when the day's target is met. Entries 3+ days old wait for your approval and aren't paid until approved.
      </>
    ),
  },
];

const CHECKS: { where: string; to: string; what: string }[] = [
  { where: "Daily sheet", to: kp.dailyWork(), what: "Every loom with a saree is listed; saving shows ₹ for each line." },
  { where: "Rounds", to: kp.rounds(), what: "Looms turn “Done” once work is saved; the saree line shows length entered." },
  { where: "Pay", to: kp.pay(), what: "Pick a worker: their days, inches and ₹ match what was entered." },
  { where: "Pay sheets", to: kp.paySheets(), what: "Dates across, people down, week total, advance cut and net due." },
  { where: "Reports", to: kp.reports(), what: "Totals per person, design and loom; Excel download." },
  { where: "Sarees", to: kp.sarees(), what: "Each saree's loom history; link it to an HKP product." },
];

/** A plain-language map of Karkhana: the flow, who does what, and where to check it. */
const KarkhanaGuidePage = () => (
  <div className="gi-registry-page kw-page kg-page">
    <PageHeader title="How Karkhana works" back={kp.more()} />
    <p className="kw-lede">The loom sheet, in five steps. Set up once; after that it's one screen a day and one sheet a week.</p>

    <ol className="kg-flow">
      {FLOW.map((s) => (
        <li key={s.n}>
          <span className="kg-flow__n" aria-hidden="true">
            {s.n}
          </span>
          <div>
            <strong>{s.title}</strong>
            <p>{s.body}</p>
          </div>
        </li>
      ))}
    </ol>

    <h3 className="kg-h">Questions</h3>
    <div className="kg-faq">
      {FAQ.map((f) => (
        <details key={f.q}>
          <summary>{f.q}</summary>
          <p>{f.a}</p>
        </details>
      ))}
    </div>

    <h3 className="kg-h">Where to check it's right</h3>
    <div className="kh-tiles">
      {CHECKS.map((c) => (
        <Link key={c.to} to={c.to} className="kh-tile">
          <strong>{c.where}</strong>
          <span>{c.what}</span>
        </Link>
      ))}
    </div>
  </div>
);

export default KarkhanaGuidePage;
